import { useLayoutEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { getDomainQueryParamsRedirect } from './domainConfig';

/**
 * Keeps the current domain's default query params on the URL for every route the app opens, not
 * only the ones that remember to add them. On edge.thriwe.com that is `userVersion=v2`, which is
 * what puts the `User-Version` header on the API calls (see Containers/Config/index.js) - a
 * hard-coded `history.push('/v2/...')`, or a page that rebuilds the query string from its own
 * filters, used to drop it and the next screen then called the API without the header.
 *
 * It corrects the URL from a layout effect rather than by rendering a `<Redirect>`: a redirect
 * would unmount the layout below it for a render, flickering the sidebar and re-fetching its
 * configuration on every corrected navigation. A layout effect runs before the pages below fire
 * their own (passive) effects, and `history.replace` updates `window.location` synchronously - so
 * the requests those effects make already read the param off the URL.
 *
 * The first URL of the session (refresh, bookmark, pasted link) is corrected even earlier, before
 * React renders at all, by `applyDomainQueryParamsToLocation` in src/index.js.
 *
 * Login and the other pre-auth screens are left alone - see `excludedPaths` in domainConfig.
 * On every domain other than edge there are no default params, so this does nothing.
 */
const DomainQueryParams = ({ children }) => {
  const history = useHistory();
  const location = useLocation();

  const corrected = getDomainQueryParamsRedirect(location?.pathname, location?.search);
  const hash = location?.hash ?? '';

  useLayoutEffect(() => {
    if (corrected) {
      history.replace(`${corrected}${hash}`);
    }
  }, [corrected, hash, history]);

  return children;
};

export default DomainQueryParams;
